/** Producer roster — Owner / Partner Director / Executive / Associate
 *  / Hired producers. Only the PH admin can add / edit / remove.
 */
import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Users, Pencil, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

export default function ProducersPanel({ org, isAdmin = false }) {
  const [items, setItems]   = useState([]);
  const [roles, setRoles]   = useState([]);
  const [suggested, setSuggested] = useState("owner");
  const [editing, setEditing] = useState(null);   // record when editing, {} when creating
  const [form, setForm] = useState({ name: "", email: "", role: "owner" });

  const load = useCallback(async () => {
    if (!org?.id) return;
    try {
      const [r, rs] = await Promise.all([
        api.get(`/orgs/${org.id}/producers`),
        api.get("/production-house/producer-roles"),
      ]);
      setItems(r.data.items || []);
      setRoles(rs.data.roles || []);
      setSuggested(r.data.suggested_role_for_next || "owner");
    } catch (e) {
      console.warn("[producers panel]", e?.message);
    }
  }, [org?.id]);

  useEffect(() => { void load(); }, [load]);

  const openNew  = () => { setForm({ name: "", email: "", role: suggested });
                            setEditing({}); };
  const openEdit = (p) => { setForm({ name: p.name || "", email: p.email || "",
                                      role: p.role || "executive_producer" });
                            setEditing(p); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Producer name is required");
    try {
      if (editing?.id) {
        await api.patch(`/orgs/${org.id}/producers/${editing.id}`, form);
        toast.success("Producer updated");
      } else {
        await api.post(`/orgs/${org.id}/producers`, form);
        toast.success("Producer added");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the producer roster?`)) return;
    try {
      await api.delete(`/orgs/${org.id}/producers/${p.id}`);
      toast.success("Producer removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const roleLabel = (k) => roles.find((r) => r.key === k)?.label || k;
  const canEdit = isAdmin;

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="producers-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 inline-flex items-center gap-1">
            <Users size={11}/> Producer Roster
          </div>
          <div className="font-display text-xl uppercase tracking-tight">
            {items.length} on the register
          </div>
          {items.length === 0 && (
            <div className="text-xs text-white/50 mt-1 inline-flex items-center gap-1">
              <Info size={11}/> No producers yet — you (the admin) are the sole signatory until you add one.
            </div>
          )}
        </div>
        {canEdit && (
          <Button onClick={openNew}
                  className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"
                  data-testid="producer-add-btn">
            <Plus size={13}/> Add producer
          </Button>
        )}
      </div>

      {items.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-white/10">
          <table className="w-full text-left">
            <thead className="bg-white/5">
              <tr className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                <th className="px-3 py-2.5">Name</th>
                <th className="px-3 py-2.5">Role</th>
                <th className="px-3 py-2.5">Email</th>
                {canEdit && <th className="px-3 py-2.5"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-white/5"
                    data-testid={`producer-row-${p.id}`}>
                  <td className="px-3 py-2.5 text-sm">{p.name}</td>
                  <td className="px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] text-white/75">
                    {roleLabel(p.role)}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-white/55">{p.email || "—"}</td>
                  {canEdit && (
                    <td className="px-3 py-2.5 text-right">
                      <Button variant="ghost" onClick={() => openEdit(p)}
                              className="h-7 px-2 text-white/60"
                              data-testid={`producer-edit-${p.id}`}>
                        <Pencil size={13}/>
                      </Button>
                      <Button variant="ghost" onClick={() => remove(p)}
                              className="h-7 px-2 text-rose-300"
                              data-testid={`producer-remove-${p.id}`}>
                        <Trash2 size={13}/>
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">
              {editing?.id ? "Edit Producer" : "Add Producer"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Producer roster entry for the production house.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="producer-name-input"/>
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     placeholder="producer@ph.com"
                     data-testid="producer-email-input"/>
            </div>
            <div>
              <Label>Role</Label>
              <select value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md bg-black/40 border border-white/15 px-3 text-sm text-white"
                      data-testid="producer-role-select">
                {roles.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
              {(roles.find((r) => r.key === form.role)?.note) && (
                <div className="text-[11px] text-white/45 mt-1">
                  {roles.find((r) => r.key === form.role).note}
                </div>
              )}
              {!editing?.id && form.role !== suggested && (
                <div className="text-[10px] text-amber-300 mt-1 tracking-[0.18em] uppercase">
                  Suggested for next hire: {roleLabel(suggested)}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save}
                    className="bg-white text-black hover:bg-white/90"
                    data-testid="producer-save-btn">
              {editing?.id ? "Save changes" : "Add to roster"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
